<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts">
    // @ts-nocheck
    import tabIcon from "@/assets/vardplatskalender.svg";

    export default {
        name: "Vardplatskalender",
        tabName: "Vårdplatskalender",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { klinikenApi, getConfig, getErrorMessage } from "@/api";
    import CalendarDropArea from "@/components/calendars/DropArea/CalendarDropArea.vue";
    import { NO_RESOURCE, SPARRAD_TID } from "@/components/calendars/helpers";
    import KCalendar from "@/components/calendars/KCalendar.vue";
    import { convertToSubsetName } from "@/store/modules/helpers/baseSubset";
    import { INTID_DEFAULT, SAL_PREFIX, SANG_PREFIX, UTTID_DEFAULT } from "@/tabs/Vardplatsbokning/helpers";
    import VardplatsTimelineEvent from "@/tabs/Vardplatsbokning/VardplatsTimelineEvent.vue";
    import { flashMessage, flashWarning, openDialog } from "@/utils";
    // Ignores behövs, pga kaosiga versioner av TS, Vite, Vue osv...
    // @ts-ignore
    import { AllowFunc, EventDropArg, EventInput } from "@fullcalendar/core";
    // @ts-ignore
    import { EventImpl } from "@fullcalendar/core/internal";
    // @ts-ignore
    import { EventResizeDoneArg } from "@fullcalendar/interaction/index.js";
    import cloneDeep from "lodash/cloneDeep";
    import moment from "moment";
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import { useStore } from "@/store";
    import { fetchStructuredAvdelningar } from "@/tabs/Vardplatsbokning/avdelningarHelpers";
    import {
        VardplatsAction,
        HanteraVardplatsOption,
        Slutenvardsbesok,
        Anhorigbesok,
        VardplatsStatus,
    } from "@/tabs/Vardplatsbokning/types";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import HanteraVardplats from "@/tabs/Vardplatsbokning/HanteraVardplats.vue";
    import StrykVardplats from "@/tabs/Vardplatsbokning/StrykVardplats.vue";
    import Vardplatsbestallning from "@/tabs/Vardplatsbokning/Vardplatsbestallning.vue";

    interface Avdelning {
        alias: string;
        id: string;
        salar: {
            id: string;
            namn: string;
            sangar: {
                id: string;
                name: string;
            }[];
        }[];
    }

    interface Resource {
        id: string;
        title: string;
        children: Array<{
            id: string;
            title: string;
            children?: Array<{
                id: string;
                title: string;
            }>;
        }>;
    }

    const store = useStore();

    const props = defineProps<{ tabId: number | string }>();

    const { tabId } = toRefs(props);

    const showSalar = ref(true);

    const selectedItem = ref<Slutenvardsbesok | Anhorigbesok | undefined>();

    const showHanteraVardplats = ref<boolean>(false);

    const hanteraVardplatsOption = ref<HanteraVardplatsOption>(HanteraVardplatsOption.DEFAULT);

    const showStrykVardplats = ref<boolean>(false);

    /**
     * Alla avdelningar med rum och sängar
     */
    const avdelningar = ref<Avdelning[]>([]);
    /**
     * Valt datum i kalendern
     */
    const currentCalendarDate = ref<Date | null>(null);

    const genderOptions = [
        {
            value: 0,
            label: "Okänt",
        },
        {
            value: 1,
            label: "Man",
        },
        {
            value: 2,
            label: "Kvinna",
        },
        {
            value: 9,
            label: "Ej tillämpligt",
        },
    ];

    /*
        Samling som låter koden översätta ett id som inte är ett nummer till ett nummer
        vid anrop till getEventColor.
        Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
        key id som ska kollas mot och value index som ska användas vid hämtning av färg
        T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
     */
    const colorKeyIndices = ref<{ [key: string]: Map<string, number> }>({});

    const fetchOperatorOptions = async () => {
        try {
            const response = await klinikenApi.get("/core/externavardpersoner/", getConfig({}));

            const options = response.data.results.map((item: { id: string; fornamn: string; efternamn: string }) => {
                return {
                    value: item.id,
                    label: `${item.fornamn} ${item.efternamn}`,
                };
            });

            // Sätter rätt index på operatörers id, för att kunna sätta färg via getEventColor
            colorKeyIndices.value = {
                ...cloneDeep(colorKeyIndices.value),
                operator: new Map(
                    options.map((option: { value: string; label: string }, index: number) => [option.value, index + 1])
                ),
            };

            return options;
        } catch (error) {
            console.error(error);

            flashWarning("Kunde inte hämta externa vårdpersoner");

            return [];
        }
    };

    const fetchVardgivareOptions = async () => {
        try {
            const response = await klinikenApi.get("/core/externavardgivare/", getConfig({}));

            const options = response.data.results.map((item: { id: string; alias: string }) => {
                return {
                    value: item.id,
                    label: item.alias,
                };
            });

            // Sätter rätt index på operatörers id, för att kunna sätta färg via getEventColor
            colorKeyIndices.value = {
                ...cloneDeep(colorKeyIndices.value),
                vardgivare: new Map(
                    options.map((option: { value: string; label: string }, index: number) => [option.value, index + 1])
                ),
            };

            return options;
        } catch (error) {
            console.error(error);

            flashWarning("Kunde inte hämta externa vårdpersoner");

            return [];
        }
    };

    /**
     * Avdelningar, rum och sängar formaterade enligt hur Fullcalendar vill ha datan
     */
    const resources = computed<Resource[]>(() => {
        if (avdelningar?.value) {
            return avdelningar.value.map((avdelning) => ({
                id: String(avdelning.id),
                title: avdelning.alias,
                extendedProps: {
                    laneClasses: ["grey-out"],
                    labelClasses: ["grey-out"],
                },
                children: (() => {
                    const salar = avdelning.salar.map((sal) => ({
                        // Children i Fullcalendar behöver ha ett helt unikt id, inte bara inom parent...
                        id: `${SAL_PREFIX}${sal.id}`,
                        title: `Sal ${sal.namn}`,
                        extendedProps: {
                            laneClasses: ["grey-out"],
                            labelClasses: ["grey-out"],
                        },
                        children: sal.sangar.map((sang) => {
                            return {
                                // Children i Fullcalendar behöver ha ett helt unikt id, inte bara inom parent...
                                id: `${SANG_PREFIX}${sang.id}`,
                                // @ts-ignore
                                title: sang.namn,
                            };
                        }),
                    }));

                    if (showSalar.value) {
                        return salar;
                    }

                    return salar.map((sal) => sal.children).flat();
                })(),
            }));
        }

        return [];
    });

    /**
     * Funktion för att hämta besök som ska användas i kalendern för att skapa events. Eventuella anhörigbesök ligger
     * inuti resp. slutenvårdsbesök.
     * Datan används även för overlay panel och andra delar som ska visa information från besöket.
     */
    const fetchData: (props?: {
        currentDate?: Date;
        range?: { start: Date; end: Date };
        viewType?: string;
        forceRefresh?: boolean;
        hasCache?: boolean;
        setSubsetName?: (props: { name: string }) => {
            hasCache?: boolean;
        };
    }) => Promise<Map<string, unknown> | { total: number; nextPage: number; subsetName: string }> = async ({
        currentDate,
        forceRefresh,
        hasCache,
        setSubsetName,
    } = {}) => {
        if (!currentDate) {
            return {
                total: 0,
                nextPage: 0,
                subsetName: "",
            };
        }

        try {
            const filterParams = {
                indatum__lte: moment(currentDate).format("YYYY-MM-DD"),
                utdatum__gte: moment(currentDate).format("YYYY-MM-DD"),
                show_strukna: false,
            };

            /*
                Sätter subset name i förväg för att tillåta rendering av det som redan ligger i store
                innan ny hämtning
             */
            const hasCache =
                setSubsetName?.({ name: convertToSubsetName({ params: filterParams }) as string })?.hasCache ?? false;

            // Ska inte visa laddningsanimation om det redan finns något att visa
            if (!hasCache) {
                await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: true });
            }

            currentCalendarDate.value = currentDate;

            const { nextPage, total, subsetName } = await store.dispatch("slutenvardsbesok/fetchSlutenvardsbesok", {
                /*
                    TODO Ska inte vara hårdkodat till true när vi har en lösning för att invalidera data i store och
                    göra nya automatiska hämtningar
                 */
                forceRefresh: true,
                page: 1,
                limit: 0,
                filterParams,
            });

            return {
                total,
                nextPage,
                subsetName,
            };
        } catch (error) {
            console.error(error);

            flashWarning("Kunde inte hämta beställningar");

            return new Map();
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: false });
        }
    };

    const extractAnhorigbesok = ({ items }: { items: Map<string | number, any> }) => {
        const result = new Map();

        Array.from(items.values()).forEach((slutenvardsbesok: any) => {
            result.set(slutenvardsbesok.id, slutenvardsbesok);

            if (slutenvardsbesok.anhorigbesok?.aktiv) {
                result.set(slutenvardsbesok.anhorigbesok.id, slutenvardsbesok.anhorigbesok);
            }
        });

        return result;
    };

    /**
     * Funktion för att hämta spärrade tider som ska användas i kalendern för att skapa events.
     */
    const fetchSparradeTiderData: (props?: {
        currentDate?: Date;
        range?: { start: Date; end: Date };
        forceRefresh?: boolean;
        hasCache?: boolean;
        setSubsetName?: (props: { name: string }) => {
            hasCache?: boolean;
        };
    }) => Promise<Map<string, unknown> | { total: number; nextPage: number; subsetName: string }> = async ({
        currentDate,
        forceRefresh,
        hasCache,
        setSubsetName,
    } = {}) => {
        if (!currentDate) {
            return {
                total: 0,
                nextPage: 0,
                subsetName: "",
            };
        }

        try {
            const filterParams = {
                startdatum__lte: moment(currentDate).format("YYYY-MM-DD"),
                slutdatum__gte: moment(currentDate).format("YYYY-MM-DD"),
            };

            /*
                Sätter subset name i förväg för att tillåta rendering av det som redan ligger i store
                innan ny hämtning
             */
            const hasCache =
                setSubsetName?.({ name: convertToSubsetName({ params: filterParams }) as string })?.hasCache ?? false;

            const { nextPage, total, subsetName } = await store.dispatch(
                "vardplatsSparradTid/fetchVardplatsSparradeTider",
                {
                    /*
                    TODO Ska inte vara hårdkodat till true när vi har en lösning för att invalidera data i store och
                    göra nya automatiska hämtningar
                 */
                    forceRefresh: true,
                    page: 1,
                    limit: 0,
                    filterParams,
                }
            );

            return {
                total,
                nextPage,
                subsetName,
            };
        } catch (error) {
            console.error(error);

            flashWarning("Kunde inte hämta spärrade tider");

            return new Map();
        }
    };

    /**
     *
     * Funktion som tar emot strukturen på objekt som hämtats från backend (data) och gör om till format som
     * Fullcalendar vill ha för att skapa events
     * @param value
     * @param props
     */
    const eventConverter: (props: { value: unknown }) => Partial<any> = ({ value, ...props }) => {
        const besok = value as any;

        const isEditable = !besok.inskriven_tidpunkt && !besok.utskriven_tidpunkt;

        let extendedProps: { [key: string]: any } = {};

        if (besok.patient) {
            // Slutenvårdsbesök
            extendedProps = {
                gender: Number(besok.patient.gender),
                operator: besok.vardplatsbestallning.operator.id,
                vardgivare: besok.vardplatsbestallning.vardgivare.id,
                anhorigbesokId: besok.anhorigbesok?.id,
                isAnhorigbesok: false,
            };
        } else {
            // Anhörigbesök
            extendedProps = {
                namn: besok.namn,
                slutenvardsbesokId: besok.slutenvardsbesok,
                patient_personId_formatted: besok.patient_personId_formatted,
                patient_namn_reverse: besok.patient_namn_reverse,
                isAnhorigbesok: true,
                noFilter: true,
                // Färg-index som overridear eventuell annan färg som sätts av filtreringen
                colorIndex: -1,
            };
        }

        if (besok.status === VardplatsStatus.UTSKRIVEN) {
            extendedProps.colorIndex = -1;
            extendedProps.faded = true;
        }

        return {
            id: String(besok.id),
            // Används inte. Visuella titeln sätts i event-komponenten
            title: besok.id,
            start: `${besok.indatum}T${besok.intid ? besok.intid : INTID_DEFAULT}`,
            end: `${besok.utdatum}T${besok.uttid ? `${besok.uttid}` : UTTID_DEFAULT}`,
            resourceId: typeof besok.sang?.id === "number" ? `${SANG_PREFIX}${besok.sang.id}` : NO_RESOURCE,
            editable: isEditable,
            startEditable: isEditable,
            resourceEditable: isEditable,
            durationEditable: isEditable,
            extendedProps,
        };
    };

    /**
     *
     * Funktion som tar emot strukturen på objekt som hämtats från backend (data) och gör om till format som
     * Fullcalendar vill ha för att skapa events
     * @param value
     * @param props
     */
    const sparradTidEventConverter: (props: { value: unknown }) => Partial<any> = ({ value, ...props }) => {
        const sparradTid = value as any;

        return {
            id: String(sparradTid.id),
            // Används inte. Visuella titeln sätts i event-komponenten
            title: sparradTid.id,
            start: `${sparradTid.startdatum}T${sparradTid.starttid}`,
            end: `${sparradTid.slutdatum}T${sparradTid.sluttid}`,
            resourceId: `${SANG_PREFIX}${sparradTid.sang.id}`,
            editable: false,
            startEditable: false,
            resourceEditable: false,
            durationEditable: false,
            extendedProps: {},
        };
    };

    /**
     * Funktion som anropas för att kolla om eventet kan släppas över en specifik del av kalendern, i det här fallet
     * kollar den på om eventet släpps på en rad för säng och inte på avdelning/rum
     */
    const eventAllow: AllowFunc = (dropInfo) => {
        const resourceId = (dropInfo as typeof dropInfo & { resource?: { id: string } }).resource?.id;

        return !!resourceId?.startsWith(SANG_PREFIX);
    };

    /**
     * Uppdaterar bokningen och returnerar den kompletta förändrade bokningen
     * @param id Id för besöket
     * @param sangId Id för sängplatsen
     * @param isAnhorigbesok Om besöket är ett anhörigbesök eller inte
     */
    const updateBokning = async ({
        id,
        sangId,
        isAnhorigbesok,
    }: {
        id: string | number;
        sangId?: string;
        isAnhorigbesok: boolean;
    }) => {
        try {
            // Sätt sang till null om no-resource eller om sangId inte är satt, för att rensa värdet i BE
            const sang = sangId === NO_RESOURCE || !sangId ? null : Number(sangId.replace(SANG_PREFIX, ""));

            if (isAnhorigbesok) {
                await store.dispatch("slutenvardsbesok/partialUpdateAnhorigbesok", {
                    anhorigbesok: {
                        id,
                        sang,
                    },
                });
            } else {
                await store.dispatch("slutenvardsbesok/partialUpdateSlutenvardsbesok", {
                    slutenvardsbesok: {
                        id,
                        sang,
                    },
                });
            }

            flashMessage(sang ? "Vårdplats bokad" : "Vårdplats avbokad");
            /*
                Returnerar bara true, eftersom värdet inte används när kalendern är kopplad via store.
                Dock måste något truthy för att indikera att allt gick rätt till
             */
            return true;
        } catch (error) {
            openDialog("Kunde inte boka vårdplatsen. " + getErrorMessage(error), "error");
        }
    };

    /**
     * Funktion som filtrerar vilka events som ska visas i CalendarDropArea (de events som ska visas i rutan ovanför
     * och ska kunna dras ner till kalendern)
     */
    const dropAreaFilter: (props: { event: EventInput }) => boolean = ({ event }) => {
        return !!(
            !event.id?.startsWith(SPARRAD_TID) &&
            event.resourceId?.startsWith(NO_RESOURCE) &&
            moment(event.start).format("yyyy-MM-DD") === moment(currentCalendarDate.value).format("yyyy-MM-DD")
        );
    };

    /**
     * Funktion som anropas när ett event utanför kalendern dras in till kalendern. Anropar backend för att spara
     * ändringar och hämta senaste datan baserat på ändringarna. Returnerar samma struktur på objektet som fetchData
     */
    const onExternalDrop = async ({
        event,
        currentData,
    }: {
        event: EventImpl;
        currentData: { [key: string]: unknown };
    }) => {
        // @ts-ignore
        const resourceId = (event as EventImpl & { getResources: () => Array<{ id: string }> }).getResources()?.[0]?.id;

        return await updateBokning({
            id: event.id,
            sangId: resourceId,
            isAnhorigbesok: event.extendedProps.isAnhorigbesok,
        });
    };

    const onEventDrop = async (arg: EventDropArg) => {
        return await updateBokning({
            id: arg.event.id,
            sangId: (arg as EventDropArg & { newResource?: { id: string } }).newResource?.id,
            isAnhorigbesok: arg.event.extendedProps.isAnhorigbesok,
        });
    };

    const onEventResize = (arg: EventResizeDoneArg) => {
        arg.revert();
        openDialog(
            "För att ändra start- eller sluttid på en bokning behöver du öppna Hantera vårdplats och uppdatera tiderna.",
            "warning"
        );
    };

    const filterTriggers = [
        {
            fieldName: "colorKey",
            dependentOn: ["gender", "vardgivare", "operator"],
            action: ({
                dependentOnValue,
                value,
            }: {
                dependentOnValue: {
                    gender: number[];
                    vardgivare: string[];
                    operator: string[];
                };
                value: string;
            }) => {
                const newColorKey =
                    Object.entries(dependentOnValue as { [key: string]: unknown[] }).find(
                        ([name, value]) => value?.length
                    )?.[0] ?? value;

                return { value: newColorKey };
            },
        },
        /*
            Rensa gender om vardgivare eller operator har valts av användaren.
            Bara en grupp av checkboxes ska kunna väljas samtidigt
         */
        {
            fieldName: "gender",
            dependentOn: ["vardgivare-k-checkbox-toggle", "operator-k-checkbox-toggle"],
            action: ({
                dependentOnValue,
                value,
            }: {
                dependentOnValue: {
                    "vardgivare-k-checkbox-toggle": boolean;
                    "operator-k-checkbox-toggle": boolean;
                };
                value: number[];
            }) => {
                return {
                    value:
                        dependentOnValue["vardgivare-k-checkbox-toggle"] ||
                        dependentOnValue["operator-k-checkbox-toggle"]
                            ? []
                            : value,
                };
            },
        },
        /*
            Rensa vardgivare om gender eller operator har valts av användaren.
            Bara en grupp av checkboxes ska kunna väljas samtidigt
         */
        {
            fieldName: "vardgivare",
            dependentOn: ["gender-k-checkbox-toggle", "operator-k-checkbox-toggle"],
            action: ({
                dependentOnValue,
                value,
            }: {
                dependentOnValue: {
                    "operator-k-checkbox-toggle": boolean;
                    "gender-k-checkbox-toggle": boolean;
                };
                value: string[];
            }) => {
                return {
                    value:
                        dependentOnValue["gender-k-checkbox-toggle"] || dependentOnValue["operator-k-checkbox-toggle"]
                            ? []
                            : value,
                };
            },
        },
        /*
            Rensa operator om vardgivare eller gender har valts av användaren.
            Bara en grupp av checkboxes ska kunna väljas samtidigt
         */
        {
            fieldName: "operator",
            dependentOn: ["vardgivare-k-checkbox-toggle", "gender-k-checkbox-toggle"],
            action: ({
                dependentOnValue,
                value,
            }: {
                dependentOnValue: {
                    "vardgivare-k-checkbox-toggle": boolean;
                    "gender-k-checkbox-toggle": boolean;
                };
                value: string[];
            }) => {
                return {
                    value:
                        dependentOnValue["vardgivare-k-checkbox-toggle"] || dependentOnValue["gender-k-checkbox-toggle"]
                            ? []
                            : value,
                };
            },
        },
        {},
    ];

    const setActivePatient = async (slutenvardsbesok: Slutenvardsbesok) => {
        if (slutenvardsbesok.patient) {
            selectedItem.value = slutenvardsbesok;
            await store.dispatch("patientData/loadFromBackend", {
                id: slutenvardsbesok.patient.id,
                failSilently: false,
            });

            await store.dispatch("alertInformation/loadAlertInformationSymbol", {
                patientId: slutenvardsbesok.patient.id,
            });
            await store.dispatch("alertInformation/loadAlertInformationList", {
                patientId: slutenvardsbesok.patient.id,
            });
        }
    };

    const openVardplatsbestallning = async (slutenvardsbesok: Slutenvardsbesok) => {
        await setActivePatient(slutenvardsbesok);
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", Vardplatsbestallning, { root: true });
        store.dispatch("tabs/openTab", {
            component: Vardplatsbestallning,
            parameters: { id: slutenvardsbesok.vardplatsbestallning.id },
        });
    };

    const hanteraVardplats = async (slutenvardsbesok: Slutenvardsbesok, option: HanteraVardplatsOption) => {
        await setActivePatient(slutenvardsbesok);
        hanteraVardplatsOption.value = option;
        selectedItem.value = slutenvardsbesok;
        showHanteraVardplats.value = true;
    };

    const strykVardplats = async (slutenvardsbesok: Slutenvardsbesok) => {
        await setActivePatient(slutenvardsbesok);
        selectedItem.value = slutenvardsbesok;
        showStrykVardplats.value = true;
    };

    const panelAction = (payload: { action: string; data: Slutenvardsbesok }) => {
        switch (payload.action) {
            case VardplatsAction.OPEN_VARDPLATSBESTALLNING:
                openVardplatsbestallning(payload.data);
                break;
            case VardplatsAction.HANTERA_VARDPLATS:
                hanteraVardplats(payload.data, HanteraVardplatsOption.DEFAULT);
                break;
            case VardplatsAction.STRYK_VARDPLATS:
                strykVardplats(payload.data);
                break;
            case VardplatsAction.SKRIV_IN_PATIENT:
                hanteraVardplats(payload.data, HanteraVardplatsOption.SKRIV_IN);
                break;
            case VardplatsAction.SKRIV_UT_PATIENT:
                hanteraVardplats(payload.data, HanteraVardplatsOption.SKRIV_UT);
                break;
            default:
                break;
        }
    };

    const defaultFilterGetter = () => ({ colorKey: "gender", gender: genderOptions.map((option) => option.value) });

    onBeforeMount(async () => {
        store.commit("vardplatsSparradTid/addSubscriber");
        store.commit("slutenvardsbesok/addSubscriber");
        avdelningar.value = await fetchStructuredAvdelningar();

        // TODO Det här ska vara en inställning i framtiden
        showSalar.value = false;
    });

    onBeforeUnmount(() => {
        store.commit("slutenvardsbesok/removeSubscriber");
        store.commit("vardplatsSparradTid/removeSubscriber");
    });
</script>

<template>
    <div class="vardplatskalender">
        <KCalendar
            :tabId="tabId"
            :eventConverter="eventConverter"
            :sparradTidEventConverter="sparradTidEventConverter"
            initialView="avdelningDayTimeline"
            :resources="resources"
            :fetchData="fetchData"
            :fetchSparradeTiderData="fetchSparradeTiderData"
            :eventAllow="eventAllow"
            :eventDurationEditable="false"
            :eventStartEditable="false"
            resourceAreaHeaderContent="Vårdplatser"
            defaultColorKey="gender"
            :navNextOverride="{
                amount: 1,
                unit: 'days',
            }"
            :navPrevOverride="{
                amount: 1,
                unit: 'days',
            }"
            :dropAreaSet="{
                name: 'resources',
                value: [NO_RESOURCE],
            }"
            :dropAreaId="`calendar-drop-area-${tabId}`"
            :onExternalDrop="onExternalDrop"
            :onEventDrop="onEventDrop"
            storePath="slutenvardsbesok/slutenvardsbesokSubset"
            sparradeTiderStorePath="vardplatsSparradTid/vardplatsSparradeTiderSubset"
            @panelAction="panelAction"
            :filterTriggers="filterTriggers"
            :colorKeyIndices="colorKeyIndices"
            :defaultFilterGetter="defaultFilterGetter"
            :weekends="true"
            :dataConverter="extractAnhorigbesok"
            :onEventResize="onEventResize"
        >
            <template #extra="{ events, colorKey, data, viewType, colorKeyIndices }">
                <CalendarDropArea
                    :id="`calendar-drop-area-${tabId}`"
                    :events="events"
                    :colorKey="colorKey"
                    :colorKeyIndices="colorKeyIndices"
                    :data="data"
                    :filter="dropAreaFilter"
                    :viewType="viewType"
                    restrictDates
                    title="Beställda"
                >
                    <template #event="{ event, data, colorKey, viewType, colorKeyIndices }">
                        <VardplatsTimelineEvent
                            v-if="viewType === 'avdelningDayTimeline'"
                            :event="event"
                            :data="data"
                            :colorKey="colorKey"
                            :colorKeyIndices="colorKeyIndices"
                            @panelAction="panelAction"
                        />
                    </template>
                </CalendarDropArea>
            </template>
            <template #filter>
                <KText name="colorKey" :style="{ display: 'none' }" />
                <div class="form-content">
                    <div class="form-section span-all divider">
                        <KCheckbox
                            allowToggle
                            useColorKey
                            name="gender"
                            toggle-label="Kön"
                            value-type="number"
                            ignoreEmptyData
                            :options="genderOptions"
                        />
                    </div>
                    <div class="form-section span-all divider">
                        <KCheckbox
                            allowToggle
                            useColorKey
                            ignoreEmptyData
                            name="vardgivare"
                            toggle-label="Vårdgivare"
                            :options="fetchVardgivareOptions"
                        />
                    </div>
                    <div class="form-section span-all divider">
                        <KCheckbox
                            allowToggle
                            useColorKey
                            ignoreEmptyData
                            name="operator"
                            toggle-label="Operatör"
                            :options="fetchOperatorOptions"
                        />
                    </div>
                </div>
            </template>
        </KCalendar>
        <ModalContent
            title="Stryk vårdplats"
            v-if="showStrykVardplats && selectedItem"
            @close="showStrykVardplats = false"
            showCloseButton
        >
            <StrykVardplats :slutenvardsbesokId="selectedItem.id" @close="showStrykVardplats = false" />
        </ModalContent>
        <ModalContent
            title="Hantera vårdplats"
            v-if="showHanteraVardplats && selectedItem"
            @close="showHanteraVardplats = false"
            showCloseButton
        >
            <HanteraVardplats
                :slutenvardsbesokId="selectedItem.id"
                :option="hanteraVardplatsOption"
                @close="showHanteraVardplats = false"
            />
        </ModalContent>
    </div>
</template>

<style scoped lang="scss">
    .vardplatskalender {
        min-height: 100%;
        display: flex;
        flex-grow: 1;
    }
</style>
